import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import MarketForm from './MarketForm';
import RowActionsMenu from '../../../shared/RowActionsMenu';
import { ActiveStatusPill } from '../../../shared/Pill';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import { ChevronRight } from 'lucide-react';
import '../../../shared/ListRowHover.css';
import '../../../shared/StyledInput.css';
import { useScreenAccess } from '../../../shared/PermissionsContext';
import SaveButton from '../../../shared/SaveButton';
import CancelButton from '../../../shared/CancelButton';
import { askConfirm } from '../../../shared/AppDialogs';

const rowInputStyle = { padding: '5px 8px', borderRadius: 5, border: '1px solid #bfc8d6', fontSize: 13 };
const linkBtnStyle = { background: 'none', border: 'none', padding: 0, color: '#1976d2', textDecoration: 'underline', cursor: 'pointer', fontSize: 13 };

// One Market's Segments, expanded inline under its row — same shape as
// Relationship Types' Stage panel (RelationshipTypeTable.js), so the whole
// Market > Segment hierarchy is managed from one screen.
function SegmentPanel({ market, showError, dismissError, onChanged }) {
  const navigate = useNavigate();
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [addName, setAddName] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchSegments = () => {
    setLoading(true);
    axios.get('/api/market-segments', { params: { market_id: market.id, status: 'all' } })
      .then((res) => setSegments(res.data.data || []))
      .catch(() => setSegments([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchSegments();
    // eslint-disable-next-line
  }, [market.id]);

  const afterChange = () => {
    fetchSegments();
    onChanged();
    dismissError();
  };

  const saveEdit = async (segment) => {
    if (!editName.trim()) return;
    setSaving(true);
    try {
      await axios.put(`/api/market-segments/${segment.id}`, { market_id: market.id, name: editName.trim() });
      setEditingId(null);
      afterChange();
    } catch (err) {
      showError(err, 'Save failed');
    }
    setSaving(false);
  };

  const toggleActive = async (segment) => {
    if (segment.is_active && !(await askConfirm(`Deactivate Market Segment "${segment.name}"? It can no longer be added to an Organisation. Organisations already in it keep it.`, { confirmLabel: 'Deactivate' }))) return;
    try {
      await axios.patch(`/api/market-segments/${segment.id}/${segment.is_active ? 'deactivate' : 'reactivate'}`);
      afterChange();
    } catch (err) {
      showError(err, `${segment.is_active ? 'Deactivate' : 'Reactivate'} failed`);
    }
  };

  const addSegment = async () => {
    if (!addName.trim()) return;
    setSaving(true);
    try {
      await axios.post('/api/market-segments', { market_id: market.id, name: addName.trim() });
      setAddName('');
      afterChange();
    } catch (err) {
      showError(err, 'Add failed');
    }
    setSaving(false);
  };

  return (
    <div style={{ padding: '4px 16px 16px 48px', background: '#f7f9fc' }}>
      {loading ? (
        <div style={{ fontSize: 13, color: '#888' }}>Loading Segments...</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, maxWidth: 720 }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', padding: '4px 6px', color: '#5a6b85', fontWeight: 600 }}>Segment</th>
              <th style={{ textAlign: 'center', padding: '4px 6px', color: '#5a6b85', fontWeight: 600, width: 90 }}>In Use</th>
              <th style={{ textAlign: 'center', padding: '4px 6px', color: '#5a6b85', fontWeight: 600, width: 90 }}>Status</th>
              <th style={{ width: 90 }}></th>
            </tr>
          </thead>
          <tbody>
            {segments.map((s) => (
              <tr key={s.id} style={{ borderTop: '1px solid #e3e7ee' }}>
                {editingId === s.id ? (
                  <td style={{ padding: '4px 6px' }}>
                    <input
                      className="styled-input"
                      style={rowInputStyle}
                      value={editName}
                      maxLength={100}
                      onChange={(e) => setEditName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(s); }}
                    />
                  </td>
                ) : (
                  <td style={{ padding: '4px 6px' }}>{s.name}</td>
                )}
                <td style={{ padding: '4px 6px', textAlign: 'center' }}>
                  {s.usage_count ? (
                    <button
                      type="button"
                      style={linkBtnStyle}
                      title="See which Organisations are in this Market Segment"
                      onClick={() => navigate(`/market-intel-all-entities?market_segment_id=${s.id}&market_segment_name=${encodeURIComponent(`${market.name} › ${s.name}`)}`)}
                    >
                      {s.usage_count}
                    </button>
                  ) : '—'}
                </td>
                <td style={{ padding: '4px 6px', textAlign: 'center' }}><ActiveStatusPill active={s.is_active} /></td>
                <td style={{ padding: '4px 6px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {editingId === s.id ? (
                    <>
                      <SaveButton type="button" loading={saving} disabled={!editName.trim()} onClick={() => saveEdit(s)} style={{ padding: '2px 10px', fontSize: 12 }} />
                      <CancelButton onClick={() => setEditingId(null)} style={{ padding: '2px 10px', fontSize: 12, marginLeft: 6 }} />
                    </>
                  ) : (
                    <RowActionsMenu
                      items={[
                        { key: 'edit', label: 'Edit', onClick: () => { setEditingId(s.id); setEditName(s.name); } },
                        s.is_active
                          ? { key: 'deactivate', label: 'Deactivate', danger: true, onClick: () => toggleActive(s) }
                          : { key: 'reactivate', label: 'Reactivate', onClick: () => toggleActive(s) },
                      ]}
                    />
                  )}
                </td>
              </tr>
            ))}
            <tr style={{ borderTop: '1px solid #e3e7ee' }}>
              <td style={{ padding: '6px 6px' }}>
                <input
                  className="styled-input"
                  style={rowInputStyle}
                  placeholder="New Segment name"
                  value={addName}
                  maxLength={100}
                  onChange={(e) => setAddName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') addSegment(); }}
                />
              </td>
              <td></td>
              <td></td>
              <td style={{ padding: '6px 6px', textAlign: 'right' }}>
                <button type="button" disabled={saving || !addName.trim()} onClick={addSegment} style={{ border: 'none', background: 'none', color: '#1976d2', cursor: addName.trim() ? 'pointer' : 'not-allowed', fontSize: 12, fontWeight: 600, opacity: addName.trim() ? 1 : 0.5 }}>
                  + Add
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      )}
    </div>
  );
}

// Markets — the top level of an Organisation's market classification (e.g.
// Automotive). Organisations are classified by Market, optionally narrowed
// to one of its Segments; click a Market to manage its Segments inline.
function MarketTable() {
  const { canCreate } = useScreenAccess();
  const navigate = useNavigate();
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState('active');
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line
  }, [status]);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/markets', { params: { status } });
      setRows(res.data.data || []);
    } catch (err) {
      setRows([]);
    }
    setLoading(false);
  };

  // Refresh counts after a Segment change without the Loading flash
  // collapsing the open panel.
  const refreshCounts = async () => {
    try {
      const res = await axios.get('/api/markets', { params: { status } });
      setRows(res.data.data || []);
    } catch (err) {
      // keep the current rows
    }
  };

  const handleAdd = () => {
    setEditItem(null);
    setShowForm(true);
  };

  const handleEdit = (item) => {
    setEditItem(item);
    setShowForm(true);
  };

  const handleDeactivate = async (item) => {
    if (!(await askConfirm(`Deactivate Market "${item.name}"? It and its Segments can no longer be added to an Organisation. Organisations already in it keep it.`, { confirmLabel: 'Deactivate' }))) return;
    try {
      await axios.patch(`/api/markets/${item.id}/deactivate`);
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, 'Deactivate failed');
    }
  };

  const handleReactivate = async (item) => {
    try {
      await axios.patch(`/api/markets/${item.id}/reactivate`);
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, 'Reactivate failed');
    }
  };

  const handleSave = async (data) => {
    setFormLoading(true);
    try {
      if (editItem) {
        await axios.put(`/api/markets/${editItem.id}`, data);
      } else {
        await axios.post('/api/markets', data);
      }
      setShowForm(false);
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, 'Save failed');
    }
    setFormLoading(false);
  };

  return (
    <div style={{ margin: '32px 24px 48px', background: '#fafafa', border: '1px solid #eceef1', borderRadius: 12, boxShadow: '0 1px 6px rgba(0,0,0,0.04)', paddingBottom: 32 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 0' }}>
        <h2 style={{ margin: 0, fontWeight: 600, fontSize: 20 }}>Markets</h2>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {canCreate && (
            <button
              onClick={handleAdd}
              disabled={showForm}
              title={showForm ? 'Finish editing first' : undefined}
              style={{ padding: '8px 18px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 500, cursor: showForm ? 'not-allowed' : 'pointer', opacity: showForm ? 0.6 : 1 }}
            >
              Add Market
            </button>
          )}
        </div>
      </div>
      <div style={{ margin: '8px 24px 0', fontSize: 13, color: '#5a6b85' }}>
        The Markets Organisations sell into, each split into Segments. Click a row to manage its Segments.
      </div>
      <div style={{ display: 'flex', gap: 10, margin: '16px 24px 0', alignItems: 'center', flexWrap: 'wrap' }}>
        <select className="styled-input" value={status} onChange={(e) => setStatus(e.target.value)} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14 }}>
          <option value="active">Active items</option>
          <option value="inactive">Deactivated items</option>
        </select>
      </div>

      {showForm && (
        <div style={{ background: '#f7f9fc', border: '1px solid #e3e7ee', padding: 20, margin: '18px 24px 0', borderRadius: 8 }}>
          <MarketForm
            key={editItem?.id ?? 'add'}
            initial={editItem}
            onSave={handleSave}
            onCancel={() => setShowForm(false)}
            loading={formLoading}
          />
        </div>
      )}

      <div style={{ margin: '24px 24px 0', background: '#fff', border: '1px solid #b3bbc7', borderRadius: 10, overflow: 'visible', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 16, tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '6%' }} />
            <col style={{ width: '40%' }} />
            <col style={{ width: '15%' }} />
            <col style={{ width: '14%' }} />
            <col style={{ width: '15%' }} />
            <col style={{ width: '10%' }} />
          </colgroup>
          <thead className="list-head" style={{ fontSize: 14 }}>
            <tr>
              <th style={{ padding: '12px 4px', fontWeight: 600, borderBottom: '1px solid #b3bbc7' }}></th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Name</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Segments</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}>In Use</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}>Status</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>Loading...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={6} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>No results</td></tr>
            ) : rows.map((item) => (
              <React.Fragment key={item.id}>
                <tr
                  className="list-row"
                  onClick={() => setExpandedId((cur) => (cur === item.id ? null : item.id))}
                  style={{ cursor: 'pointer' }}
                  title="Click to manage Segments"
                >
                  <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                    <ChevronRight size={16} strokeWidth={2} style={{ color: '#9aa5b5', transform: expandedId === item.id ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />
                  </td>
                  <td style={{ padding: '6px 8px', fontSize: 14, fontWeight: 600 }}>{item.name}</td>
                  <td style={{ padding: '6px 8px', fontSize: 13, color: '#5a6b85' }}>{item.segment_count} segment{item.segment_count === 1 ? '' : 's'}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'center', fontSize: 13 }} onClick={(e) => e.stopPropagation()}>
                    {item.usage_count ? (
                      <button
                        type="button"
                        style={linkBtnStyle}
                        title="See which Organisations are in this Market"
                        onClick={() => navigate(`/market-intel-all-entities?market_id=${item.id}&market_name=${encodeURIComponent(item.name)}`)}
                      >
                        {item.usage_count}
                      </button>
                    ) : '—'}
                  </td>
                  <td style={{ padding: '6px 8px', textAlign: 'center', fontSize: 13 }}>
                    <ActiveStatusPill active={item.is_active} />
                  </td>
                  <td style={{ padding: '6px 8px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                    <RowActionsMenu
                      items={[
                        { key: 'edit', label: 'Edit', disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => handleEdit(item) },
                        item.is_active
                          ? { key: 'deactivate', label: 'Deactivate', danger: true, disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => handleDeactivate(item) }
                          : { key: 'reactivate', label: 'Reactivate', disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => handleReactivate(item) },
                      ]}
                    />
                  </td>
                </tr>
                {expandedId === item.id && (
                  <tr>
                    <td colSpan={6} style={{ padding: 0 }}>
                      <SegmentPanel market={item} showError={showError} dismissError={dismissError} onChanged={refreshCounts} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default MarketTable;
